<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-card-title class="text-left card-header fixed-card-title2">
			<v-row no-gutters>
				<v-col cols="6">
					<v-toolbar-title>{{ $t('add-consent-form-header') }}</v-toolbar-title>
				</v-col>
				<v-spacer />
				<v-icon color="white" @click="closeDrawer">mdi-close</v-icon>
			</v-row>
		</v-card-title>
		<v-card-text class="pa-4 pb-0 overflow-y-auto" style="height: calc(100vh - 221px)">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-select
						:label="$t('type')"
						v-model="selectedType"
						:items="consentFormTypeTemplate"
						item-text="name"
						outlined
						hide-details
						return-object
						:menu-props="{ maxHeight: 199, offsetY: true }"
						@change="typeText = selectedType.description" />
				</v-col>
			</v-row>

			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-textarea v-model="typeText" :label="$t('description')" outlined hide-details rows="12" />
				</v-col>
			</v-row>

			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-select
						outlined
						v-model="obtainedBy"
						:label="$t('staff')"
						:items="medicalStaff"
						return-object
						item-text="fullName"
						:menu-props="{ maxHeight: 200, offsetY: true }"
						hide-details />
				</v-col>
			</v-row>

			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-file-input
						v-model="file"
						:label="$t('select-file')"
						outlined
						show-size
						:accept="extensionList.join(', ')"
						prepend-icon=""
						append-icon="mdi-file-upload"
						:rules="rules"
						@change="validateFileInput" />
				</v-col>
			</v-row>
		</v-card-text>
		<v-container class="px-4">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<InputTimeFormatter
						:inputValue="timeSpentSeconds"
						@update:inputValue="(val) => (timeSpentSeconds = val)"
						:dense="false"
						:inputLabel="$t('time-spent')" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="6" order="1">
					<v-btn @click="closeDrawer()" color="secondary" style="color: black" block>
						{{ $t('cancel') }}
					</v-btn>
				</v-col>
				<v-tooltip top v-if="!addButtonEnabled">
					<template v-slot:activator="{ on, attrs }">
						<v-col cols="6" v-bind="attrs" v-on="on" order="2">
							<v-btn disabled block>{{ $t('add') }}</v-btn>
						</v-col>
					</template>
					<span>{{ $t('fill-all-fields-warning') }}</span>
				</v-tooltip>
				<v-col cols="6" v-else order="2">
					<v-btn @click="addAttachment()" color="dark_purple" style="color: white" block>
						{{ $t('add') }}
					</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import InputTimeFormatter from '../Client/components/InputTimeFormatter.vue';

export default {
	name: 'CCMConsentAdd',
	components: { InputTimeFormatter },
	data () {
		return {
			typeText: '',
			timeSpentSeconds: 0,
			file: null,
			formValid: false,
			selectedType: null,
			consentFormTypeTemplate: null,
			medicalStaff: null,
			obtainedBy: null,
			extensionList: [
				'image/jpg',
				'image/png',
				'image/webp',
				'image/jpeg',
				'image/gif',
				'image/bmp',
				'image/svg',
				'text/plain',
				'text/csv',
				'text/html',
				'application/msword',
				'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
				'application/x-7z-compressed',
				'application/zip',
				'application/x-zip-compressed',
				'application/vnd.rar',
				'application/x-tar',
				'application/pdf',
				'application/vnd.ms-excel',
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				'application/vnd.ms-powerpoint',
				'application/vnd.openxmlformats-officedocument.presentationml.presentation',
				'application/vnd.oasis.opendocument.presentation',
				'application/vnd.oasis.opendocument.spreadsheet',
				'application/vnd.oasis.opendocument.text',
				'application/rtf',
			],
			rules: [
				(value) => !!value || this.$t('required-field'),
				(value) => !value || this.extensionList.includes(value.type) || this.$t('please-select-valid-file-type'),
				(value) => !value || value.size < 512000 || this.$t('file-size-limit') + ' 500 KB',
			],
		};
	},
	computed: {
		...mapState({
			userData: (state) => state.authentication.userData,
			currentRecord: (state) => state.ccmRecords.ccmRecord,
			detailedUser: (state) => state.users.detailedUser,
			roleList: (state) => state.roles.roles,
		}),
		addButtonEnabled () {
			return (
				this.typeText.trim() &&
				this.timeSpentSeconds &&
				this.file &&
				this.selectedType &&
				this.obtainedBy &&
				this.formValid &&
				this.timeSpentSeconds <= 7200
			);
		},
	},
	methods: {
		...mapActions({
			addCCMConsentForm: 'ccmConsentForms/addCCMConsentForm',
			getConsentTypeMethodTemplate: 'ccmRecords/getConsentTypeTemplate',
			getConsentFormsByPatientId: 'ccmConsentForms/getAllCCMConsentFormByPatientId',
		}),
		async addAttachment () {
			const request = new FormData();
			request.append('CcmRecordId', this.currentRecord.id);
			request.append('PatientId', this.currentRecord.patientId);
			request.append('HospitalId', this.currentRecord.hospitalId);
			request.append('File', this.file);
			request.append('ConsentType', this.selectedType.name);
			request.append('ConsentText', this.typeText);
			request.append('StaffId', this.obtainedBy.id);
			request.append('TimeSpentSeconds', this.timeSpentSeconds);
			request.append('ObtainedBy', this.obtainedBy.fullName);
			request.append('ObtainedByRole', this.roleList.find((item) => item.id === this.obtainedBy.roleId).roleName);
			request.append('AdditionalData', '');
			await this.addCCMConsentForm(request);
			await this.getConsentFormsByPatientId(this.detailedUser.userData.id);
			this.closeDrawer();
		},
		closeDrawer () {
			this.timeSpentSeconds = 0;
			this.notes = '';
			this.file = null;
			this.selectedType = null;
			this.typeText = '';
			this.obtainedBy = null;
			this.$store.commit('SET_DETAILS_DRAWER', false);
		},
		filterMedicalStaff () {
			return this.detailedUser.userData.medicalTeamsData[0].membersList
				.filter((member) => (member.roleId === 3 || member.roleId === 4) && member.isSequoiaUser && member.status === 1)
				.map((member) => ({
					...member,
					fullName: `${member.firstName} ${member.lastName}`,
				}));
		},
		validateFileInput (file) {
			this.formValid = file && file.size < 512000 && this.extensionList.includes(file.type);
		},
	},
	async mounted () {
		this.medicalStaff = this.filterMedicalStaff();
		if (!this.consentFormTypeTemplate) {
			const response = await this.getConsentTypeMethodTemplate();
			this.consentFormTypeTemplate = response.data;
		}
	},
};
</script>
